import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFieldArray } from 'react-final-form-arrays';
import { FormRenderProps, useField } from 'react-final-form';

import PageBaseProps from 'types/PageBase';
import { DocumentStatus, MobilityDocument } from 'modules/selfServices/types/SelfService';

import { b } from 'utils/i18nUtils';
import { required } from 'utils/formUtils';

import Card from 'components/ui/Card';
import PageFooter from 'components/ui/PageFooter';

import { isMobilityAccepted } from 'modules/selfServices/selectors';
import { UPDATE_FIXED_CACHE_KEY } from 'modules/selfServices/constants';
import { useUpdateSelfServiceMutation } from 'modules/selfServices/service';
import { booleanToYesNoChoice, YesNoChoice, yesNoChoiceToBoolean } from 'types/Question';

import Document from './Document';
import YesNoCardActions from '../ui/Card/YesNoCardActions';

const MobilityDocumentFormRender: React.FC<FormRenderProps & PageBaseProps> = ({
  isPenultimate,
  handleSubmit,
  errors = {},
  onPrev,
  shouldDisplayBackButton,
}) => {
  const intl = useIntl();
  const [, { isLoading: isUpdateLoading }] = useUpdateSelfServiceMutation({ fixedCacheKey: UPDATE_FIXED_CACHE_KEY });
  const {
    input: { value: mobilityStatus, onChange: changeMobilityStatus },
    meta
  } = useField<boolean, HTMLElement, YesNoChoice>('customerAccepted', {
    validate: required,
    subscription: { value: true, error: true, submitFailed: true },
    format: booleanToYesNoChoice,
    parse: yesNoChoiceToBoolean,
  });

  const { fields: formDocuments } = useFieldArray<MobilityDocument>('documents', { subscription: { value: true } });

  const error = meta.submitFailed && meta.error && intl.formatMessage({ id: meta.error, defaultMessage: meta.error });

  const mobilityAlreadyAccepted = useSelector(isMobilityAccepted);

  const hasValidationErrors = Object.keys(errors).length > 0;
  const documents = React.useMemo(() => {
    const documentsFormNames = formDocuments.map((name) => name) ?? [];
    const { value = [] } = formDocuments;
    return value.reduce((acc, { status }, index) => {
      if (status === DocumentStatus.REQUESTED) {
        acc.requested.push(documentsFormNames[index]);
      } else if (status === DocumentStatus.REFUSED) {
        acc.refused.push(documentsFormNames[index]);
      }

      return acc;
    }, { requested: [], refused: [] });
  }, [formDocuments]);

  const hasRefusedDocuments = documents.refused.length > 0;
  const hasRequestedDocuments = documents.requested.length > 0;
  const showRefusedDocuments = hasRefusedDocuments && mobilityAlreadyAccepted;
  const showRequestedDocuments = hasRequestedDocuments && (mobilityAlreadyAccepted || mobilityStatus === YesNoChoice.YES);

  return (
    <>
      <main className="main-content max-w-content">
        <div className="content">
          {!mobilityAlreadyAccepted && (
            <>
              <Card
                mandatory
                centerText
                error={error}
                className="mb-5"
                title={intl.formatMessage({
                  id: 'mobilityDocument.question',
                  defaultMessage: 'Do you still want a courtesy vehicle?',
                })}
              >
                <YesNoCardActions onChange={changeMobilityStatus} value={mobilityStatus} />
              </Card>
              {showRequestedDocuments && (
                <>
                  <div className="h-px w-full bg-border-default mb-5 kiosk:mb-14" />
                  <p className="text-center">
                    <FormattedMessage
                      id="mobilityDocument.verify"
                      defaultMessage="In order to provide you with a courtesy vehicle, we need to <b>verify your information.</b>"
                      values={{ b }}
                    />
                  </p>
                </>
              )}
            </>
          )}
          {showRefusedDocuments && (
            <>
              <p className="text-center mb-3">
                <FormattedMessage
                  id="mobilityDocument.refused"
                  defaultMessage="The following documents have <b>been refused :</b>"
                  values={{ b }}
                />
              </p>
              <ul className="w-full rounded-card border border-border-default divide-y">
                {documents.refused.map((name, index) =>
                  <li key={formDocuments.value[index].id}>
                    <Document name={name}/>
                  </li>
                )}
              </ul>
              <div className="h-px w-full bg-border-default kiosk:mb-14" />
            </>
          )}
          {showRequestedDocuments && (
            <>
              <p className="text-center">
                <FormattedMessage
                  id="mobilityDocument.takePicture"
                  defaultMessage="Please take pictures of the documents requested below."
                />
              </p>
              <ul className="w-full rounded-card border border-border-default divide-y mb-28">
                {documents.requested.map((name, index) =>
                  <li key={formDocuments.value[index].id}>
                    <Document name={name}/>
                  </li>
                )}
              </ul>
            </>
          )}
        </div>
      </main>
      <PageFooter
        onPrev={onPrev}
        onNext={handleSubmit}
        loading={isUpdateLoading}
        isPenultimate={isPenultimate}
        disabledStyle={hasValidationErrors}
        shouldDisplayBackButton={shouldDisplayBackButton}
      />
    </>
  );
};

export default MobilityDocumentFormRender;
